import React, { useEffect, useState } from "react";
import {
  Modal,
  Button,
  Group,
  TextInput,
  Stack,
  FileInput,
  Text,
  Radio,
  Select,
} from "@mantine/core";
import { IconUpload } from "@tabler/icons-react";
import { showNotification } from "@mantine/notifications";

interface Props {
  isOpen: boolean;
  batchNo: string | null;
  onClose: () => void;
  onUpload?: () => void;
}

const UploadResourceModal: React.FC<Props> = ({
  isOpen,
  batchNo,
  onClose,
  onUpload,
}) => {
  const [mode, setMode] = useState<"new" | "existing">("new");
  const [folderName, setFolderName] = useState("");
  const [existingFolder, setExistingFolder] = useState<string | null>(null);
  const [existingFolders, setExistingFolders] = useState<string[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);

  /* ----------------------------------
     Fetch existing folders
  -----------------------------------*/
  useEffect(() => {
    if (!isOpen || !batchNo) return;

    const fetchFolders = async () => {
      try {
        const res = await fetch(
          `https://trainer-portal-78f5.onrender.com/api/lms/resources?batchNo=${encodeURIComponent(
            batchNo
          )}`
        );
        const json = await res.json();

        const files = Array.isArray(json) ? json : json.files || [];
        const folderSet = new Set<string>();

        files.forEach((item: any) => {
          const key = item.key || item.Key;
          if (!key) return;
          const parts = key.split("/");
          if (parts[2]) folderSet.add(parts[2]);
        });

        setExistingFolders(Array.from(folderSet));
      } catch (err) {
        console.error("Failed to fetch folders", err);
        setExistingFolders([]);
      }
    };

    fetchFolders();
  }, [isOpen, batchNo]);

  /* ----------------------------------
     Upload handler
  -----------------------------------*/
  const handleUpload = async () => {
    if (!batchNo) {
      showNotification({
        title: "Error",
        message: "Batch number missing",
        color: "red",
      });
      return;
    }

    if (!files.length) {
      showNotification({
        title: "Error",
        message: "Please select at least one file",
        color: "red",
      });
      return;
    }

    const finalFolder =
      mode === "existing" ? existingFolder : folderName;

    if (!finalFolder) {
      showNotification({
        title: "Folder required",
        message:
          mode === "existing"
            ? "Please select an existing folder"
            : "Please enter a new folder name",
        color: "red",
      });
      return;
    }

    try {
      setLoading(true);

      const formData = new FormData();
      formData.append("batchNo", batchNo);
      formData.append("folderName", finalFolder);

      files.forEach((file) => {
        formData.append("files", file);
      });

      const res = await fetch(
        "https://trainer-portal-78f5.onrender.com/api/lms/upload-resource",
        {
          method: "POST",
          body: formData,
        }
      );

      if (!res.ok) throw new Error("Upload failed");

      showNotification({
        title: "Success",
        message: "Resources uploaded successfully",
        color: "green",
      });

      setFiles([]);
      setFolderName("");
      setExistingFolder(null);
      onUpload?.();
      onClose();
    } catch (error) {
      console.error(error);
      showNotification({
        title: "Error",
        message: "Failed to upload resources",
        color: "red",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      opened={isOpen}
      onClose={onClose}
      title="Upload Resources"
      centered
      size="md"
    >
      <Stack>
        <Text size="sm" fw={500}>
          Batch: {batchNo}
        </Text>

        {/* ✅ ALWAYS show both options */}
        <Radio.Group
          value={mode}
          onChange={(value) => setMode(value as "new" | "existing")}
        >
          <Stack>
            <Radio value="new" label="Create New Folder" />
            <Radio
              value="existing"
              label="Upload to Existing Folder"
              disabled={existingFolders.length === 0}
            />
          </Stack>
        </Radio.Group>

        {existingFolders.length === 0 && (
          <Text size="xs" c="dimmed">
            No existing folders found for this batch
          </Text>
        )}

        {/* 🆕 Create New Folder */}
        {mode === "new" && (
          <TextInput
            label="New Folder Name"
            placeholder="e.g. Week 1, Assignments"
            value={folderName}
            onChange={(e) => setFolderName(e.currentTarget.value)}
          />
        )}

        {/* 📁 Existing Folder */}
        {mode === "existing" && (
          <Select
            label="Select Existing Folder"
            data={existingFolders}
            value={existingFolder}
            onChange={setExistingFolder}
            placeholder={
              existingFolders.length === 0
                ? "No folders available"
                : "Choose folder"
            }
            disabled={existingFolders.length === 0}
          />
        )}

        <FileInput
          label="Select Files"
          placeholder="Choose files"
          multiple
          value={files}
          onChange={setFiles}
        />

        <Group justify="flex-end" mt="md">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            leftSection={<IconUpload size={16} />}
            loading={loading}
            onClick={handleUpload}
          >
            Upload
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default UploadResourceModal;
